import { Settings, Information, NotebookReference } from "@carbon/icons-react";
import { t } from "@lingui/macro";
import { ChevronRightIcon, MenuListItemType, MenuListItemVariant } from "@WahooFitness/redesignr";
import { useCallback, useEffect, useMemo } from "react";
import { ReactRouterLinkWrapper } from "../MenuList/ReactRouterLinkWrapper";
import { crux_bolt_type_e } from "@WahooFitness/crux-js-display-cfg";
import { getStoredValue, setStoredValue, useConfigContext } from "@WahooFitness/wahoo-offline-mfe";
import { useNavigate } from "react-router";
import useBCAutoPair from "@/hooks/useBCAutoPair";
import usePairedDevice from "@/hooks/usePairedDevice";
import useAssociatedDevices from "@/hooks/useAssociatedDevices";
import useSWR from "swr";
import { useCheckForOnboardingEffect } from "./useCheckForOnboardingEffect";
import { useSettingsRemoteConfig } from "@/hooks/useSettingsRemoteConfig";
import WSMSensorConnectionState from "@WahooFitness/wsm-native/dist/esm/types/connection_state";
import { Box, useTheme } from "@mui/material";
import SignalStrengthIcon from "./SignalStrengthIcon";
import { useRemoteSensors } from "@/hooks/useRemoteSensors";

const useDeviceConfig = (appToken: string) => {
  const { palette } = useTheme();
  const { getSettingsRemoteConfigString } = useSettingsRemoteConfig();
  const { bcAutoPairing } = useConfigContext();
  const { startPairing, pairingFailed, pairing } = useBCAutoPair();
  const navigate = useNavigate();

  const {
    device,
    deviceLoading,
    reloadDevice,
    isDeviceConnected,
    connectionStateDisplay,
    unpairDevice,
    setDeviceName,
  } = usePairedDevice(appToken);

  const { remoteSensorStatus, refreshRemoteSensorStatus, getDisplayName, getImageSrc } =
    useRemoteSensors(appToken);

  // Get out if onboarding isn't finished.
  useCheckForOnboardingEffect(appToken, isDeviceConnected);

  const { cloudDevices } = useAssociatedDevices();

  useEffect(() => {
    if (appToken && device?.serialNumber) {
      setStoredValue(`${appToken}_serialNumber`, device.serialNumber);
    }
  }, [appToken, device?.serialNumber]);

  const { data: savedSerialNumber } = useSWR(`${appToken}_serialNumber`, getStoredValue);

  const deviceInfo = useMemo(() => {
    const serialNumber = device?.serialNumber ?? savedSerialNumber;

    let firmwareVersion = device?.firmwareVersion;
    if (!firmwareVersion && serialNumber && cloudDevices) {
      const associatedDevice = cloudDevices.find((device) => device.serial_number === serialNumber);
      if (associatedDevice) {
        firmwareVersion = `${associatedDevice.firmware_version}-${associatedDevice.software_version}`;
      }
    }

    return {
      firmwareVersion: firmwareVersion ?? t`Unknown`,
      serialNumber: serialNumber ?? t`Unknown`,
    };
  }, [cloudDevices, device?.firmwareVersion, device?.serialNumber, savedSerialNumber]);

  useEffect(() => {
    if (!deviceLoading) {
      const interval = setInterval(() => {
        reloadDevice();
        refreshRemoteSensorStatus();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [deviceLoading, refreshRemoteSensorStatus, reloadDevice]);

  const remoteSetUpGuideUrls = useMemo(
    (): Record<number, string | undefined> => ({
      [crux_bolt_type_e.ROAM]: getSettingsRemoteConfigString("ROAM_1_SETUP_GUIDE_URL"),
      [crux_bolt_type_e.ROAM_1_1]: getSettingsRemoteConfigString("ROAM_1_SETUP_GUIDE_URL"),
      [crux_bolt_type_e.ROAM_2]: getSettingsRemoteConfigString("ROAM_2_SETUP_GUIDE_URL"),
      [crux_bolt_type_e.BOLT]: getSettingsRemoteConfigString("BOLT_1_SETUP_GUIDE_URL"),
      [crux_bolt_type_e.BOLT_2]: getSettingsRemoteConfigString("BOLT_2_SETUP_GUIDE_URL"),
      [crux_bolt_type_e.ACE]: getSettingsRemoteConfigString("ACE_SETUP_GUIDE_URL"),
      [crux_bolt_type_e.BOLT_3]: getSettingsRemoteConfigString("BOLT_3_SETUP_GUIDE_URL"),
      [crux_bolt_type_e.ROAM_3]: getSettingsRemoteConfigString("ROAM_3_SETUP_GUIDE_URL"),
    }),
    [getSettingsRemoteConfigString]
  );

  const setUpGuideUrl = useMemo(
    () => (device?.boltType !== undefined ? remoteSetUpGuideUrls[device.boltType] : ""),
    [device?.boltType, remoteSetUpGuideUrls]
  );

  const profilesSettingsMenu = useMemo(
    (): MenuListItemType[] => [
      {
        id: "workout-profiles",
        icon: <Settings size={24} color={palette.text.primary} />,
        content: t`Workout profiles`,
        variant: MenuListItemVariant.InternalLink,
        linkLocation: `/workout-profiles?familyFilter=0`, // Only show biking profiles
        linkComponent: ReactRouterLinkWrapper,
      },
      {
        id: "settings",
        icon: <Settings size={24} color={palette.text.primary} />,
        content: t`Settings`,
        variant: MenuListItemVariant.InternalLink,
        linkLocation: `/device-config/${appToken}/settings`,
        disabled: !isDeviceConnected,
        linkComponent: ReactRouterLinkWrapper,
      },
    ],
    [appToken, isDeviceConnected, palette.text.primary]
  );

  const remoteSensorMenu = useMemo((): MenuListItemType[] | undefined => {
    if (!isDeviceConnected || !remoteSensorStatus?.sensors) {
      return undefined;
    }
    return remoteSensorStatus?.sensors
      .filter((sensor) => sensor.status.is_paired)
      .map((sensor) => {
        const iconSrc = getImageSrc(sensor);
        return {
          id: `${sensor.remote_sensor_id[0]}`,
          icon: (
            <Box
              display="flex"
              alignContent="center"
              justifyContent="center"
              width={64}
              height={64}
              p={0.5}
              mr={1}
            >
              {iconSrc && (
                <img style={{ objectFit: "contain", maxWidth: 64 }} src={iconSrc} alt="" />
              )}
            </Box>
          ),
          content: getDisplayName(sensor),
          secondaryContent:
            sensor.status.connection_state === WSMSensorConnectionState.CONNECTED
              ? t`Connected`
              : t`Disconnected`,
          variant: MenuListItemVariant.Action,
          actionComponent: (
            <Box display="flex" flexDirection="row" gap={1}>
              {sensor.status.connection_state === WSMSensorConnectionState.CONNECTED && (
                <SignalStrengthIcon rssi={sensor.status.rssi} />
              )}
              <ChevronRightIcon sx={{ color: palette.action.active }} />
            </Box>
          ),
          action: () => navigate(`/device-config/${appToken}/sensor/${sensor.remote_sensor_id[0]}`),
        };
      });
  }, [
    appToken,
    getDisplayName,
    getImageSrc,
    isDeviceConnected,
    navigate,
    palette.action.active,
    remoteSensorStatus?.sensors,
  ]);

  const deviceInfoSetupMenu = useMemo(
    (): MenuListItemType[] => [
      {
        id: "device-information",
        icon: <Information size="24px" color={palette.text.primary} />,
        content: t`Device information`,
        variant: MenuListItemVariant.Action,
        action: () => {
          navigate(`/device-config/${appToken}/info`, {
            state: {
              firmwareVersion: deviceInfo.firmwareVersion,
              serialNumber: deviceInfo.serialNumber,
            },
          });
        },
        actionComponent: <ChevronRightIcon sx={{ color: palette.action.active }} />,
      },
      {
        id: "set-up-guide",
        icon: <NotebookReference size="24px" color={palette.text.primary} />,
        content: t`Set up guide`,
        variant: MenuListItemVariant.ExternalLink,
        linkLocation: setUpGuideUrl,
        hidden: setUpGuideUrl === undefined || setUpGuideUrl.length === 0,
      },
    ],
    [
      appToken,
      deviceInfo.firmwareVersion,
      deviceInfo.serialNumber,
      navigate,
      palette.action.active,
      palette.text.primary,
      setUpGuideUrl,
    ]
  );

  useEffect(() => {
    if (!device && bcAutoPairing && !pairingFailed && !pairing && appToken) {
      startPairing(appToken, reloadDevice);
    }
  }, [appToken, bcAutoPairing, device, pairing, pairingFailed, reloadDevice, startPairing]);

  const deviceConfigLoading = useMemo(
    () => deviceLoading || (!device && bcAutoPairing && !pairingFailed),
    [bcAutoPairing, device, deviceLoading, pairingFailed]
  );

  const refreshDeviceConfig = useCallback(() => {
    refreshRemoteSensorStatus();
  }, [refreshRemoteSensorStatus]);

  return {
    deviceConfigLoading,
    device,
    unpairDevice,
    setDeviceName,
    connectionStateDisplay,
    profilesSettingsMenu,
    remoteSensorMenu,
    deviceInfoSetupMenu,
    refreshDeviceConfig,
  };
};

export { useDeviceConfig };
